/**
 * PUT a file/blob to a GCS V4 signed URL with the metadata headers the server
 * folded into the signature. The response object MUST come from a v2 mutation
 * that returns `{ tempUrl, requestedBy, userType }` — those bytes are echoed
 * verbatim onto the request so GCS's canonical-request hash matches.
 *
 * Any drift between what the server signed and what the client sends fails
 * the V4 signature → 403 SignatureDoesNotMatch. Don't compute these header
 * values yourself; always pass them through from the mutation response.
 *
 * @param {{ tempUrl: string, requestedBy: string, userType: string }} signedUrlResponse
 * @param {File | Blob} file
 * @param {{
 *   contentType?: string,
 *   additionalMetaHeaders?: Record<string, string>,
 * }} [options]
 * @returns {Promise<Response>}
 */
export const uploadToSignedUrl = async (signedUrlResponse, file, options = {}) => {
  const { tempUrl, requestedBy, userType } = signedUrlResponse
  // Guard `instanceof File`/`Blob` for non-browser contexts (SSR / Node) where
  // these globals may be undefined; fall back to duck-typed `.type` access.
  const fileMimeType =
    (typeof Blob !== 'undefined' && file instanceof Blob) ||
    (typeof File !== 'undefined' && file instanceof File)
      ? file.type
      : undefined
  const contentType = options.contentType ?? fileMimeType ?? 'application/octet-stream'

  return fetch(tempUrl, {
    method: 'PUT',
    body: file,
    // Spread additional meta headers FIRST so the signed-URL extension headers
    // (`x-goog-meta-requested-by`, `x-goog-meta-user-type`) always win on key
    // collision — last-key-wins. A future caller passing one of those keys in
    // `additionalMetaHeaders` cannot accidentally re-introduce SignatureDoesNotMatch.
    headers: {
      'Content-Type': contentType,
      ...(options.additionalMetaHeaders ?? {}),
      'x-goog-meta-requested-by': requestedBy,
      'x-goog-meta-user-type': userType,
    },
  })
}

export const uploadImage = async (presignedUrlResponse, responseKey, croppedImage) => {
  if (!croppedImage || !presignedUrlResponse) {
    return
  }
  if (presignedUrlResponse?.[responseKey]) {
    try {
      await fetch(croppedImage)
        .then((response) => response.blob())
        .then(async (blob) => {
          await fetch(presignedUrlResponse?.[responseKey], {
            method: 'PUT',
            body: blob,
          })
            .then((uploadImageResponse) => {
              return uploadImageResponse
            })
            .catch((error) => {
              throw error
            })
        })
        .catch((error) => {
          throw error
        })
    } catch (error) {
      return error
    }
  } else {
    return
  }
}
